import React, { useRef, useEffect, useState, useCallback } from "react";
import WatermarkedMedia from "./WatermarkedMedia";
import { ChevronLeft, ChevronRight } from "lucide-react";

const DEFAULT_OFFERINGS = [
  {
    title: "Makeup Artistry",
    image: "/api/files/ai_b527daedfc1a4fdca0b27243e5766273.jpg",
  },
  {
    title: "Lighting & Camera Equipment",
    image: "/api/files/ai_ceb80248ef52488ca5a68a511bfa8c8c.jpg",
  },
  {
    title: "Global Location Scouting",
    image: "/api/files/ai_8a4ca5a15b3740bf88158bae77717860.jpg",
  },
  {
    title: "Luxury Vanity Vans",
    image: "/api/files/ai_312e43a983744e23ac9f7f7118509529.jpg",
  },
  {
    title: "Crew & Casting",
    image: "/api/files/ai_c2af02ca1a3f4b74a7560aa0ebf9db59.jpg",
  },
  {
    title: "In-House Photographers",
    image: "/api/files/ai_2ef244f672e74d24b1746af2979e2a51.jpg",
  },
  {
    title: "Film Production",
    image: "https://images.unsplash.com/photo-1485846234645-a62644f84728?auto=format&fit=crop&w=900&q=80",
  },
  {
    title: "Music Video Production",
    image: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?auto=format&fit=crop&w=900&q=80",
  },
  {
    title: "Styling Assistance",
    image: "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?auto=format&fit=crop&w=900&q=80",
  },
  {
    title: "Visa & Travel Assistance",
    image: "https://images.unsplash.com/photo-1488646953014-85cb44e25828?auto=format&fit=crop&w=900&q=80",
  },
];

export const WhatWeOffer = ({ items = DEFAULT_OFFERINGS }) => {
  const scrollerRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  const updateArrows = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 8);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  }, []);

  useEffect(() => {
    updateArrows();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [updateArrows]);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector("[data-offer-card]");
    const step = card ? card.clientWidth + 24 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <section className="relative bg-onyx-900 py-24 md:py-32 overflow-hidden" data-testid="what-we-offer-section">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Left: massive "WHAT WE OFFER" heading */}
          <div className="lg:col-span-3 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="h-px w-10 bg-gold" />
                <span className="font-cinzel text-[11px] tracking-[0.4em] uppercase text-gold">
                  Services
                </span>
              </div>
              <h2 className="font-bebas text-6xl md:text-7xl lg:text-8xl text-ivory leading-[0.92]" data-testid="offer-title">
                What We<br />
                <span className="text-gold-gradient">Offer</span>
              </h2>
              <p className="mt-6 text-ivory/60 max-w-sm leading-relaxed">
                Ten cinematic disciplines, one production house — every department under one gold seal.
              </p>
            </div>

            {/* Reel arrows */}
            <div className="mt-10 flex items-center gap-4">
              <button
                onClick={() => scrollBy(-1)}
                disabled={!canPrev}
                className="reel-arrow"
                data-testid="offer-prev"
                aria-label="Previous services"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={() => scrollBy(1)}
                disabled={!canNext}
                className="reel-arrow"
                data-testid="offer-next"
                aria-label="More services"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>

          {/* Right: portrait reel scroller */}
          <div className="lg:col-span-9 relative">
            <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-gradient-to-l from-onyx-900 to-transparent" />
            <div
              ref={scrollerRef}
              className="offer-scroller flex gap-5 overflow-x-auto pb-4 snap-x"
              style={{ scrollbarWidth: "none" }}
              data-testid="offer-reel"
            >
              {items.map((it, i) => (
                <div
                  key={it.title || `offer-${i}`}
                  data-offer-card
                  className="snap-start shrink-0 w-[68vw] sm:w-[260px] md:w-[280px] lg:w-[260px] xl:w-[280px]"
                >
                  <OfferCard offering={it} index={i} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .offer-scroller::-webkit-scrollbar { display: none; }
        .reel-arrow {
          width: 56px; height: 56px;
          border-radius: 50%;
          border: 1px solid rgba(212,175,55,0.4);
          color: #F5EFE0;
          background: transparent;
          display: inline-flex; align-items: center; justify-content: center;
          transition: all 0.4s ease;
        }
        .reel-arrow:not(:disabled):hover {
          background: #D4AF37;
          color: #050505;
          border-color: #D4AF37;
          transform: scale(1.05);
        }
        .reel-arrow:disabled { opacity: 0.25; cursor: not-allowed; }
      `}</style>
    </section>
  );
};

const OfferCard = ({ offering, index }) => (
  <div className="group" data-testid={`offer-card-${index}`}>
    <div className="relative aspect-[3/4] overflow-hidden gold-border">
      <WatermarkedMedia
        src={offering.image}
        alt={offering.title}
        position="bottom-right"
        opacity={0.8}
        watermarkSize={36}
        showCaption={false}
        className="h-full w-full transition-transform duration-700 group-hover:scale-110"
      />
      {/* Service number */}
      <div className="absolute top-4 left-4 font-bebas text-2xl text-gold/80">
        {String(index + 1).padStart(2, "0")}
      </div>
    </div>
    <h3 className="mt-5 font-bebas text-xl md:text-2xl text-ivory leading-tight group-hover:text-gold transition-colors">
      {offering.title}
    </h3>
    <div className="mt-3 h-px w-8 bg-gold/40 transition-all duration-500 group-hover:w-20 group-hover:bg-gold" />
  </div>
);

export default WhatWeOffer;
